import { type RefObject, useEffect, useLayoutEffect, useState } from 'react';
import type { Measurable } from '@radix-ui/rect';

import { addWindowEventListener, getDocument, isServer } from '@/lib/utils';

const useIsomorphicLayoutEffect = isServer ? useEffect : useLayoutEffect;

type PopoverPlacement = {
  side: 'top' | 'bottom';
  sideOffset: number;
};

const DEFAULT_SIDE_OFFSET = 8;
const COLLISION_PADDING = 16;
const DEFAULT_PLACEMENT: PopoverPlacement = { side: 'bottom', sideOffset: DEFAULT_SIDE_OFFSET };
const TOP_PLACEMENT: PopoverPlacement = { side: 'top', sideOffset: DEFAULT_SIDE_OFFSET };

export function usePopoverPlacement(
  anchorRef: RefObject<Measurable | null> | undefined,
  open: boolean,
  maxHeightPx = 700,
  maxViewportHeightRatio = 0.7,
) {
  const [placement, setPlacement] = useState<PopoverPlacement>(DEFAULT_PLACEMENT);

  useIsomorphicLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const anchor = anchorRef?.current;
      const doc = getDocument();
      const view = doc?.defaultView;
      if (!anchor || !view) return;
      const r = anchor.getBoundingClientRect();
      const required =
        Math.min(view.innerHeight * maxViewportHeightRatio, maxHeightPx) +
        COLLISION_PADDING +
        DEFAULT_SIDE_OFFSET;

      let nextPlacement = DEFAULT_PLACEMENT;
      if (view.innerHeight - r.bottom >= required) {
        nextPlacement = DEFAULT_PLACEMENT;
      } else if (r.top >= required) {
        nextPlacement = TOP_PLACEMENT;
      } else {
        const headerBottom =
          doc?.querySelector<HTMLElement>('[data-page-editor-header]')?.getBoundingClientRect()
            .bottom ?? 0;
        nextPlacement = {
          side: 'bottom',
          sideOffset: Math.round(headerBottom + DEFAULT_SIDE_OFFSET - r.bottom),
        };
      }

      setPlacement((current) =>
        current.side === nextPlacement.side && current.sideOffset === nextPlacement.sideOffset
          ? current
          : nextPlacement,
      );
    };
    update();
    const offScroll = addWindowEventListener('scroll', update, { capture: true, passive: true });
    const offResize = addWindowEventListener('resize', update);
    return () => {
      offScroll?.();
      offResize?.();
    };
  }, [open, anchorRef, maxHeightPx, maxViewportHeightRatio]);

  return placement;
}
